import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useNavigate, useParams } from "react-router-dom"
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts"
import { Eye, Clock, Play, Download } from "lucide-react"

const mockViewerData = [
  { day: "Day 1",  views: 8  },
  { day: "Day 2",  views: 5  },
  { day: "Day 3",  views: 3  },
  { day: "Day 4",  views: 4  },
  { day: "Day 5",  views: 2  },
  { day: "Day 6",  views: 1  },
  { day: "Day 7",  views: 0  },
]

const mockViewers = [
  { id: "rv1", name: "Michael Scott",  email: "michael@co.com",  watchedAt: "Apr 18, 10:30 AM", duration: "72 min", progress: 80 },
  { id: "rv2", name: "Pam Beesly",     email: "pam@co.com",      watchedAt: "Apr 18, 02:15 PM", duration: "45 min", progress: 50 },
  { id: "rv3", name: "Jim Halpert",    email: "jim@co.com",       watchedAt: "Apr 19, 09:00 AM", duration: "90 min", progress: 100 },
  { id: "rv4", name: "Dwight Schrute", email: "dwight@co.com",   watchedAt: "Apr 19, 11:20 AM", duration: "60 min", progress: 67 },
  { id: "rv5", name: "Andy Bernard",   email: "andy@co.com",      watchedAt: "Apr 20, 03:45 PM", duration: "30 min", progress: 33 },
]

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-xl shadow-lg p-3 text-xs">
      <p className="font-semibold text-slate-800 dark:text-white mb-1">{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ color: p.stroke }} className="mt-0.5">{p.name}: <strong>{p.value}</strong></p>
      ))}
    </div>
  )
}

export default function RecordingView() {
  const t = useTranslation()
  const navigate = useNavigate()
  const { id }   = useParams()

  return (
    <div className="flex flex-col h-full overflow-y-auto w-full bg-background p-4 md:p-6 gap-5">

      {/* Breadcrumb + Header */}
      <div>
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-3">
          <button onClick={() => navigate("/webinar")} className="text-primary font-semibold hover:text-primary transition-colors">{t("webinar.webinar")}</button>
          <span>/</span>
          <button onClick={() => navigate(`/webinar/${id}/analytics`)} className="text-primary font-semibold hover:text-primary transition-colors">{t("webinar.analytics")}</button>
          <span>/</span>
          <span className="text-slate-700 dark:text-slate-200 font-semibold">{t("webinar.recordingViews")}</span>
        </div>

        <div className="flex items-center gap-3">
          {/* <button
            onClick={() => navigate(`/webinar/${id}/analytics`)}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 text-slate-600 dark:text-slate-400" />
          </button> */}
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{t("webinar.recordingViews")}</h1>
            <p className="text-xs text-slate-400 mt-0.5">{t("webinar.postWebinarRecordingAnalytics")}</p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
          { label: t("webinar.totalViews"),    value: "23",     icon: <Eye   className="w-4 h-4 text-blue-500" />,    bg: "bg-blue-50   dark:bg-blue-900/20"   },
          { label: t("webinar.avgWatchTime"),value: "54 min", icon: <Clock className="w-4 h-4 text-emerald-500" />, bg: "bg-emerald-50 dark:bg-emerald-900/20" },
          { label: t("webinar.fullCompletions"),value: "3",     icon: <Play  className="w-4 h-4 text-purple-500" />,  bg: "bg-purple-50 dark:bg-purple-900/20" },
        ].map(c => (
          <div key={c.label} className="bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-2xl p-4 shadow-sm">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${c.bg}`}>{c.icon}</div>
            <div className="text-2xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{c.value}</div>
            <div className="text-xs text-slate-400 mt-0.5">{c.label}</div>
          </div>
        ))}
      </div>

      {/* View Trend Chart */}
      <div className="bg-card dark:bg-card rounded-2xl border border-stroke-100 dark:border-border dark:border-border shadow-sm p-5">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-foreground/80 dark:text-white mb-1">{t("webinar.recordingViewsTrend")}</h3>
        <p className="text-xs text-slate-400 mb-4">{t("webinar.viewsOverFirst7Days")}</p>
        <div className="h-[180px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={mockViewerData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="viewGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#0ea5e9" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
              <XAxis dataKey="day"   fontSize={11} stroke="currentColor" />
              <YAxis               fontSize={11} stroke="currentColor" allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone" dataKey="views" name="Views"
                stroke="#0ea5e9" strokeWidth={2}
                fill="url(#viewGrad)"
                dot={{ r: 4, fill: "#0ea5e9" }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Viewer Table */}
      <div className="bg-card dark:bg-card rounded-2xl border border-stroke-100 dark:border-border dark:border-border shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-stroke-100 dark:border-border dark:border-border">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">{t("webinar.viewers")}</h3>
          <button className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 hover:text-primary transition-colors">
            <Download className="w-3.5 h-3.5" /> {t("webinar.export")}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 dark:bg-card/80 border-b border-stroke-100 dark:border-border dark:border-border">
                {[t("webinar.viewer"), t("webinar.email"), t("webinar.watchedAt"), t("webinar.watchTime"), t("webinar.progress")].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mockViewers.map(v => (
                <tr key={v.id} className="border-b border-slate-50 dark:border-border/40 hover:bg-slate-50 dark:hover:bg-slate-700/20 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary to-violet-500 flex items-center justify-center text-white text-[11px] font-bold shrink-0">
                        {v.name[0]}
                      </div>
                      <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">{v.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">{v.email}</td>
                  <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">{v.watchedAt}</td>
                  <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">{v.duration}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden min-w-[60px]">
                        <div
                          className="h-full bg-primary rounded-full transition-all"
                          style={{ width: `${v.progress}%` }}
                        />
                      </div>
                      <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 w-8">{v.progress}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
